import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes for long simulations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    if (error.response) {
      // Server responded with error code
      throw new Error(`Server error: ${error.response.data.detail || error.response.statusText}`);
    } else if (error.request) {
      // Request was sent but no response received
      throw new Error('Server unavailable. Check your internet connection.');
    } else {
      // Something went wrong while setting up the request
      throw new Error(`Request error: ${error.message}`);
    }
  }
);

// API methods for simulation
export const simulationAPI = {
  // Run simulation
  runSimulation: async (config, poolDistribution = null) => {
    try {
      const response = await api.post('/api/simulation/run', {
        config,
        pool_distribution: poolDistribution
      });
      return response.data;
    } catch (error) {
      throw new Error(`Simulation start error: ${error.message}`);
    }
  },

  // Run Monte Carlo over current engine
  runMonteCarlo: async (payload, { samples = 200, parallel = true, mode = 'engine' } = {}) => {
    try {
      const params = new URLSearchParams({
        samples: String(samples),
        parallel: String(parallel),
        mode
      });
      const response = await api.post(`/api/simulation/run-monte-carlo?${params.toString()}` , payload);
      return response.data;
    } catch (error) {
      throw new Error(`Monte Carlo error: ${error.message}`);
    }
  },

  // Test Cases API
  listTestCases: async () => {
    try {
      const response = await api.get('/api/simulation/test-cases');
      return response.data;
    } catch (error) {
      throw new Error(`Error listing test cases: ${error.message}`);
    }
  },

  getTestCase: async (testId) => {
    try {
      const response = await api.get(`/api/simulation/test-cases/${testId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Error getting test case: ${error.message}`);
    }
  },

  createTestCase: async ({ name, description, config, pool_distribution, monte_carlo_results }) => {
    try {
      const response = await api.post('/api/simulation/test-cases', {
        name,
        description,
        config,
        pool_distribution,
        monte_carlo_results
      });
      return response.data;
    } catch (error) {
      throw new Error(`Error creating test case: ${error.message}`);
    }
  },

  updateTestCase: async (testId, { name, description, config, pool_distribution, monte_carlo_results }) => {
    try {
      const response = await api.put(`/api/simulation/test-cases/${testId}`, {
        name,
        description,
        config,
        pool_distribution,
        monte_carlo_results
      });
      return response.data;
    } catch (error) {
      throw new Error(`Error updating test case: ${error.message}`);
    }
  },

  deleteTestCase: async (testId) => {
    try {
      const response = await api.delete(`/api/simulation/test-cases/${testId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Error deleting test case: ${error.message}`);
    }
  },

  updateTestCaseMonteCarloResults: async (testId, results) => {
    try {
      const response = await api.post(`/api/simulation/test-cases/${testId}/monte-carlo-results`, results);
      return response.data;
    } catch (error) {
      throw new Error(`Error updating Monte Carlo results: ${error.message}`);
    }
  },

  // Validate configuration
  validateConfig: async (config) => {
    try {
      const response = await api.post('/api/simulation/validate-config', config);
      return response.data;
    } catch (error) {
      throw new Error(`Validation error: ${error.message}`);
    }
  },

  // Get default configuration
  getDefaultConfig: async () => {
    try {
      const response = await api.get('/api/simulation/default-config');
      return response.data;
    } catch (error) {
      throw new Error(`Default configuration retrieval error: ${error.message}`);
    }
  },

  // Get default pool distribution
  getDefaultPoolDistribution: async () => {
    try {
      const response = await api.get('/api/simulation/default-pool-distribution');
      return response.data;
    } catch (error) {
      throw new Error(`Pool distribution retrieval error: ${error.message}`);
    }
  },

  // Quick simulation test
  quickTest: async () => {
    try {
      const response = await api.post('/api/simulation/quick-test');
      return response.data;
    } catch (error) {
      throw new Error(`Quick test error: ${error.message}`);
    }
  },

  // Check API health
  healthCheck: async () => {
    try {
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      throw new Error(`API health check error: ${error.message}`);
    }
  }
};

export default api;
